import type { FC, ReactNode } from 'react'

import { useEffect, useMemo, useRef } from 'react'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { storyParagraphs } from '@/constants/full-service'

type Token = { kind: 'word', text: string } | { kind: 'icon' }

const Burst: FC = (): ReactNode => (
    <svg viewBox='0 0 24 24' fill='none' aria-hidden='true' className='size-[0.56em] animate-spin-slow text-violet'>
        {Array.from({ length: 8 }, (_, index) => {
            const angle = (index * Math.PI) / 4
            const round = (value: number) => Math.round(value * 100) / 100
            const x1 = round(12 + Math.cos(angle) * 3.5)
            const y1 = round(12 + Math.sin(angle) * 3.5)
            const x2 = round(12 + Math.cos(angle) * 9.5)
            const y2 = round(12 + Math.sin(angle) * 9.5)

            return <line key={index} x1={x1} y1={y1} x2={x2} y2={y2} stroke='currentColor' strokeWidth='2.6' strokeLinecap='round' />
        })}
    </svg>
)

// The paragraph starts light gray; each word turns near-black as the visitor scrolls through the section.
export const ScrollStory: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const tokenRefs = useRef<Array<HTMLSpanElement | null>>([])
    const reduced = useReducedMotion()

    const paragraphs = useMemo<Array<Array<Token>>>(() => storyParagraphs.map((paragraph, paragraphIndex) => {
        const tokens: Array<Token> = []

        paragraph.split(' ').forEach((word, wordIndex) => {
            tokens.push({ kind: 'word', text: word })

            if (paragraphIndex === 0 && wordIndex === 1) tokens.push({ kind: 'icon' })
        })

        return tokens
    }), [])

    const total = paragraphs.reduce((sum, tokens) => sum + tokens.length, 0)

    useEffect(() => {
        const light = (count: number) => {
            tokenRefs.current.forEach((token, index) => {
                if (!token) return

                const next = index < count ? 'true' : 'false'

                if (token.dataset.lit !== next) token.dataset.lit = next
            })
        }

        if (reduced) {
            light(total)
            return
        }

        let frame = 0

        const update = () => {
            frame = 0

            const section = sectionRef.current

            if (!section) return

            const rect = section.getBoundingClientRect()
            const viewport = window.innerHeight
            const sticky = window.matchMedia('(min-width: 768px)').matches
            const progress = sticky
                ? (viewport * 0.35 - rect.top) / Math.max(1, rect.height - viewport + viewport * 0.2)
                : (viewport * 0.8 - rect.top) / Math.max(1, rect.height + viewport * 0.45)

            light(Math.round(Math.min(1, Math.max(0, progress)) * total))
        }

        const schedule = () => {
            if (!frame) frame = window.requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', schedule)

        return () => {
            window.cancelAnimationFrame(frame)
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
        }
    }, [reduced, total])

    let tokenIndex = 0

    return (
        <section ref={sectionRef} id='story' aria-label='Why FlowCraft keeps the whole crew in-house' className='relative border-t border-hairline md:h-[230vh]'>
            <div className='flex items-center py-24 md:sticky md:top-0 md:h-screen md:py-0'>
                <div className='fc-container'>
                    <div className='mx-auto flex max-w-[900px] flex-col gap-7 text-[clamp(1.5rem,3.1vw,2.625rem)] font-medium leading-[1.24] tracking-[-0.03em] md:gap-9'>
                        {paragraphs.map((tokens, paragraphIndex) => (
                            <p key={paragraphIndex}>
                                {tokens.map(token => {
                                    const index = tokenIndex

                                    tokenIndex += 1

                                    if (token.kind === 'icon') {
                                        return (
                                            <span
                                                key={`icon-${index}`}
                                                ref={element => {
                                                    tokenRefs.current[index] = element
                                                }}
                                                data-lit='false'
                                                aria-hidden='true'
                                                className='mr-[0.25em] inline-flex size-[0.92em] translate-y-[0.12em] scale-75 items-center justify-center rounded-full bg-violet-soft opacity-40 ring-1 ring-inset ring-[#e2d8ff] transition-[scale,opacity] duration-500 ease-out data-[lit=true]:scale-100 data-[lit=true]:opacity-100'
                                            >
                                                <Burst />
                                            </span>
                                        )
                                    }

                                    return (
                                        <span
                                            key={`word-${index}`}
                                            ref={element => {
                                                tokenRefs.current[index] = element
                                            }}
                                            data-lit='false'
                                            className='text-[#cfcfd7] transition-colors duration-300 ease-out data-[lit=true]:text-ink'
                                        >
                                            {token.text}{' '}
                                        </span>
                                    )
                                })}
                            </p>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}
